/*!
 * 聯盟 P&L 調整（v2.49.0）
 *
 * 每一週：ClubGG 原始輸贏 → 聯盟調整 → 最終輸贏，逐俱樂部、逐玩家看得到。
 * 只有兩種事會改輸贏（Brad 2026-09-28 定案）：
 *   ① 用票補回：玩家用了我方送的票報名 MTT → 補回票面（只動 MTT）
 *   ② 實體獎勵扣除：iPhone / 實體門票由聯盟直接發 → 扣掉得主的 MTT 獎金
 * 歷史週（v2.49.0 前）另有兩種只給人看的列：
 *   對帳有記、實際未套用 —— 票券對帳算了補償，但重新匯入把它洗掉了（當時的 bug）
 *   無法歸因差額 —— 現在的值與 ClubGG 原始值的差，找不到對應的調整
 *
 * 資料：GET /api/tiny/pl-adjustments/weeks、/week/<week>（Backend/api/tiny/pl_adjustments.py）
 * 樣式一律走 constant/adminTheme；loading 照 TinyLoading.js 檔頭的四情境規則。
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Table } from "reactstrap";
import axios from "axios";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyStatStrip from "components/tiny/TinyStatStrip";
import TinyWeekSelector from "components/tiny/TinyWeekSelector";
import TinyTooltip from "components/tiny/TinyTooltip";
import TinyLoading, { TinyLoadingOverlay } from "components/tiny/TinyLoading";
import { COLORS, thStyle, tdStyle, softBadge, mutedBadge } from "constant/adminTheme";
import TinyPhysicalPrizeReview from "./TinyPhysicalPrizeReview";

const API = "/api/tiny/pl-adjustments";

const fmt = (v) =>
  v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const signed = (v) => (v > 0 ? `+${fmt(v)}` : fmt(v));
const apiError = (e) => e?.response?.data?.msg || e?.message || "發生錯誤";
const TYPE_LABELS = { credit: "信用制", deposit: "儲值制" };

const KIND_COLOR = {
  ticket_comp: COLORS.success,
  physical_prize: COLORS.info,
  legacy_unexplained: COLORS.textMuted,
};

const numTd = { ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const numTh = { ...thStyle, textAlign: "right" };

function AmountCell({ value, strong }) {
  if (!value) return <td style={{ ...numTd, color: COLORS.textDimmed }}>—</td>;
  return (
    <td style={{ ...numTd, fontWeight: strong ? 700 : 500, color: value > 0 ? COLORS.success : COLORS.danger }}>
      {signed(value)}
    </td>
  );
}

function itemTip(it) {
  const d = it.detail || {};
  const lines = [];
  if (d.mtt_name) lines.push(`${d.mtt_name}（${(d.start_time || "").replace("T", " ").slice(0, 16)}）`);
  if (d.ticket_cost) lines.push(`票面 ${fmt(d.ticket_cost)}`);
  if (d.event) lines.push(d.event);
  if (d.prize_mtt) lines.push(`得獎賽事：${d.prize_mtt}`);
  if (d.note) lines.push(d.note);
  if (it.status !== "applied") lines.push("⚠ 票券對帳有算，但重新匯入把它洗掉了，當時寄出的報表沒有這筆");
  return lines.map((t, i) => <div key={i}>{t}</div>);
}

function ItemBadge({ it }) {
  const notApplied = it.status !== "applied";
  const style = notApplied
    ? softBadge(COLORS.warning, { marginRight: 4, display: "inline-block", marginBottom: 2 })
    : it.kind === "legacy_unexplained"
      ? { ...mutedBadge, marginRight: 4, display: "inline-block", marginBottom: 2 }
      : softBadge(KIND_COLOR[it.kind] || COLORS.accent, { marginRight: 4, display: "inline-block", marginBottom: 2 });
  return (
    <TinyTooltip content={<div>{itemTip(it)}</div>}>
      <span style={{ ...style, cursor: "help" }}>
        {notApplied ? "未套用 " : ""}{it.kind_label} {signed(it.amount)}
      </span>
    </TinyTooltip>
  );
}

function TinyPlAdjustments() {
  const [weeks, setWeeks] = useState([]);
  const [week, setWeek] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clubFilter, setClubFilter] = useState(null);
  const [tab, setTab] = useState("detail");

  useEffect(() => {
    axios.get(`${API}/weeks`)
      .then((res) => {
        const ws = res.data?.data || [];
        setWeeks(ws);
        if (ws.length) setWeek(ws[0].week);
      })
      .catch((e) => setError(apiError(e)));
  }, []);

  useEffect(() => {
    if (!week) return;
    setLoading(true);
    setClubFilter(null);
    axios.get(`${API}/week/${week}`)
      .then((res) => {
        if (res.data?.code !== 1) throw new Error(res.data?.msg);
        setData(res.data.data);
        setError(null);
      })
      .catch((e) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, [week]);

  const totals = useMemo(() => {
    const t = { ticket_comp: 0, physical_prize: 0, other: 0, not_applied: 0 };
    (data?.clubs || []).forEach((c) => Object.keys(t).forEach((k) => { t[k] += c[k] || 0; }));
    return t;
  }, [data]);

  const players = useMemo(
    () => (data?.players || []).filter((p) => !clubFilter || p.club_id === clubFilter),
    [data, clubFilter],
  );

  const weekOptions = useMemo(() => weeks.map((w) => w.week), [weeks]);
  const verify = data?.verify;

  const stats = [
    { label: "用票補回（玩家輸贏加回）", value: signed(totals.ticket_comp), icon: "fa-ticket", color: "45, 206, 137" },
    { label: "實體獎勵扣除（得主獎金扣掉）", value: signed(totals.physical_prize), icon: "fa-gift", color: "17, 205, 239" },
  ];
  if (totals.not_applied) {
    stats.push({ label: "對帳有記、實際未套用（歷史）", value: signed(totals.not_applied), icon: "fa-triangle-exclamation", color: "251, 99, 64" });
  }
  if (totals.other) {
    stats.push({ label: "無法歸因差額（歷史）", value: signed(totals.other), icon: "fa-circle-question", color: "160, 160, 160" });
  }

  return (
    <div className="content">
      <TinyPageHeader
        title="聯盟 P&L 調整"
        description="ClubGG 原始輸贏經過聯盟調整後的最終輸贏。只有兩種調整：玩家用了我方送的票（補回 MTT 輸）、實體獎勵由聯盟直接發（扣掉得主的 MTT 獎金）。"
        reserveScope
        scope={weekOptions.length > 0 ? (
          <TinyWeekSelector label="" options={weekOptions} value={week} onChange={setWeek} showMeta={false} />
        ) : null}
        tabs={{
          items: [
            { key: "detail", label: "調整明細", icon: "fa fa-list" },
            { key: "prize", label: "實體獎勵確認", icon: "fa fa-gift" },
          ],
          active: tab,
          onChange: setTab,
        }}
      />

      {tab === "prize" && week && <TinyPhysicalPrizeReview week={week} />}

      {tab === "detail" && error && <Alert color="danger">{error}</Alert>}
      {tab === "detail" && !data && loading && <TinyLoading text="讀取調整明細" />}

      {tab === "detail" && data && (
        <TinyLoadingOverlay active={loading} text="重新整理">

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
            {verify && (verify.ok ? (
              <span style={softBadge(COLORS.success)}>✓ 最終輸贏 ＝ ClubGG 原始 ＋ 聯盟調整</span>
            ) : (
              <TinyTooltip content={<div>{(verify.problems || []).map((p, i) => <div key={i}>{p}</div>)}</div>}>
                <span style={{ ...softBadge(COLORS.danger), cursor: "help" }}>✗ 輸贏檢查不通過（{verify.summary?.bad} 筆）</span>
              </TinyTooltip>
            ))}
            {data.frozen && (
              <TinyTooltip content="這週的報表在調整帳上線前就已寄出，數字維持當時的樣子，不會被自動改寫。">
                <span style={{ ...mutedBadge, cursor: "help" }}>已寄出 · 凍結</span>
              </TinyTooltip>
            )}
            {data.raw_source === "assumed_current" && (
              <TinyTooltip content="主機上沒有這週的 ClubGG 原始檔，無法拆出調整前的數字；原始欄顯示的是目前的值扣掉已知調整。">
                <span style={{ ...mutedBadge, cursor: "help" }}>無 ClubGG 原始檔</span>
              </TinyTooltip>
            )}
          </div>

          <TinyStatStrip items={stats} />

          <h5 style={{ color: COLORS.textSecondary, marginTop: 6 }}>俱樂部</h5>
          {data.clubs.length === 0 ? (
            <div style={{ color: COLORS.textMuted, padding: "12px 0 20px" }}>這週沒有任何聯盟調整。</div>
          ) : (
            <div style={{ overflowX: "auto", marginBottom: 24 }}>
              <Table size="sm" style={{ color: COLORS.textPrimary, minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>俱樂部</th>
                    <th style={thStyle}>制別</th>
                    <th style={numTh}>ClubGG 原始輸贏</th>
                    <th style={numTh}>用票補回</th>
                    <th style={numTh}>實體獎勵扣除</th>
                    {totals.other ? <th style={numTh}>無法歸因</th> : null}
                    <th style={numTh}>最終輸贏</th>
                    {totals.not_applied ? (
                      <th style={numTh}>
                        <TinyTooltip content="票券對帳有算、但當時被重新匯入洗掉的補償。不在最終輸贏裡，寄出的報表也沒有這筆，只列出來給人看。">
                          <span style={{ cursor: "help" }}>未套用（歷史）ⓘ</span>
                        </TinyTooltip>
                      </th>
                    ) : null}
                    <th style={numTh}>玩家</th>
                  </tr>
                </thead>
                <tbody>
                  {data.clubs.map((c) => {
                    const active = clubFilter === c.club_id;
                    return (
                      <tr key={c.club_id}
                          onClick={() => setClubFilter(active ? null : c.club_id)}
                          style={{ cursor: "pointer", background: active ? "rgba(17,205,239,0.08)" : undefined }}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>
                          {c.club_name || c.club_id}
                          <span style={{ color: COLORS.textDimmed, fontSize: 11, marginLeft: 6 }}>{c.club_id}</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={c.club_type === "credit" ? softBadge(COLORS.success) : softBadge(COLORS.accent)}>
                            {TYPE_LABELS[c.club_type] || "—"}
                          </span>
                          {c.club_type === "deposit" && (
                            <span style={{ color: COLORS.textDimmed, fontSize: 11, marginLeft: 6 }}>P&L 不交收</span>
                          )}
                        </td>
                        <td style={numTd}>{fmt(c.raw_pl)}</td>
                        <AmountCell value={c.ticket_comp} />
                        <AmountCell value={c.physical_prize} />
                        {totals.other ? <AmountCell value={c.other} /> : null}
                        <td style={{ ...numTd, fontWeight: 700 }}>{fmt(c.final_pl)}</td>
                        {totals.not_applied ? (
                          <td style={{ ...numTd, color: c.not_applied ? COLORS.warning : COLORS.textDimmed }}>
                            {c.not_applied ? signed(c.not_applied) : "—"}
                          </td>
                        ) : null}
                        <td style={numTd}>{c.players}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {data.players.length > 0 && (
            <>
              <h5 style={{ color: COLORS.textSecondary }}>
                玩家明細
                {clubFilter && (
                  <span style={{ ...mutedBadge, marginLeft: 10, cursor: "pointer" }} onClick={() => setClubFilter(null)}>
                    只看 {data.clubs.find((c) => c.club_id === clubFilter)?.club_name || clubFilter} ✕
                  </span>
                )}
              </h5>
              <div style={{ overflowX: "auto" }}>
                <Table size="sm" style={{ color: COLORS.textPrimary, minWidth: 1100 }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>玩家</th>
                      <th style={thStyle}>俱樂部</th>
                      <th style={thStyle}>超代 / 代理</th>
                      <th style={numTh}>MTT 輸贏（ClubGG）</th>
                      <th style={numTh}>聯盟調整</th>
                      <th style={numTh}>MTT 輸贏（最終）</th>
                      <th style={numTh}>總輸贏（最終）</th>
                      <th style={thStyle}>調整明細</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map((p) => (
                      <tr key={`${p.club_id}|${p.player_id}`}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600 }}>{p.nickname || "—"}</div>
                          <div style={{ fontFamily: "monospace", fontSize: 11, color: COLORS.textMuted }}>{p.player_id}</div>
                        </td>
                        <td style={tdStyle}>{p.club_name || p.club_id}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: COLORS.textSecondary }}>
                          <div>{p.super_agent || "—"}</div>
                          <div style={{ color: COLORS.textMuted }}>{p.agent || ""}</div>
                        </td>
                        <td style={numTd}>{fmt(p.raw_mtt)}</td>
                        <AmountCell value={p.adjustment} strong />
                        <td style={{ ...numTd, fontWeight: 700 }}>{fmt(p.final_mtt)}</td>
                        <td style={numTd}>{fmt(p.final_total)}</td>
                        <td style={{ ...tdStyle, whiteSpace: "normal", minWidth: 260 }}>
                          {p.items.map((it, i) => <ItemBadge key={i} it={it} />)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </TinyLoadingOverlay>
      )}
    </div>
  );
}

export default TinyPlAdjustments;
